<template>
  <div>
    <div v-if="isLoadingMessages" class="text-center mt-5">
      <small class="align-middle">Loading chat messages...</small>
    </div>
    <div
      ref="chatMessage"
      v-if="totalMessages >= 0"
      class="chat-box bg-white is-floating"
      :class="`chat-box-room-${room.id} ${isFloating ? 'pt-2 px-3' : ''}`"
      :style="setChatMessageStyle"
      @scroll="scrollMessages"
    >
      <div v-for="(group, minute) in groupedMessages" :key="minute">
        <div v-for="(message, index) in group" :key="message.id">
          <template v-if="message.user_id === account_id">
            <chat-message-receiver
              :is-floating="isFloating"
              :avatar="account_image"
              :message="message"
              :show-time="index === group.length - 1"
              :show-arrow="index === 0"
            />
          </template>
          <template v-else>
            <chat-message-sender
              :is-floating="isFloating"
              :message="message"
              :show-time="index === group.length - 1"
              :show-arrow="index === 0"
            />
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { eventBus } from "../../../utils/helpers";
import { getChatMessages, getMessage, getMoreChatMessages } from "../../../services/api/uni-chat";
import ChatMessageReceiver from "./ChatMessageReceiver.vue";
import ChatMessageSender from "./ChatMessageSender.vue";

export default {
  name: "ConversationMessage",
  components: { ChatMessageSender, ChatMessageReceiver },
  props: {
    isFloating: {
      type: Boolean,
      default: true
    },
    isCollapsed: {
      type: [Boolean, Number],
      default: false
    },
    room: {
      type: [Array, Object],
      default: []
    },
    height: {
      type: Number,
      default: 0
    }
  },
  data() {
    return {
      account_id: 0,
      account_name: '',
      account_image: '',
      isLoadingMessages: false,
      isLoadingMoreMessages: false,
      isAllMessagesLoaded: false,
      messages: [],
      messageCounter: 15,
      message: [],
      totalMessages: 0,
      channel: null,
      prevScrollTop: 0
    };
  },
  created() {
    this.getMessages(this.room.id);
    this.channel = new BroadcastChannel('chat');
    this.channel.onmessage = this.sendingMessage;

    eventBus.$on('message-sent', this.messageSent);
  },
  beforeDestroy() {
    this.channel.close();
    eventBus.$off('message-sent', this.messageSent);
  },
  computed: {
    setChatMessageStyle() {
      return {
        height: this.isFloating ? this.height + 'px' : 'auto',
        overflowY: this.isFloating ? 'none' : 'auto'
      };
    },
    groupedMessages() {
      let sequence = 1;

      return this.messages.reduce((grouped, message, index, array) => {
        const timestamp = new Date(message.created_at);
        const messageType = message.user_id === this.account_id ? 'receiver' : 'sender';
        const key = `${timestamp.toISOString().slice(0, 16)} ${messageType} ${sequence}`;

        grouped[key] = grouped[key] || [];
        grouped[key].push(message);

        const nextMessage = array[index + 1];
        const sameUser = nextMessage && nextMessage.user_id === message.user_id;

        if (!sameUser || (nextMessage && new Date(nextMessage.created_at) - timestamp > 60000)) {
          sequence++;
        }

        return grouped;
      }, {});
    }
  },
  watch: {
    room: {
      handler(newRoom, oldRoom) {
        if (newRoom.id !== oldRoom.id) {
          this.getMessages(newRoom.id);
        }
      },
      deep: true
    },
    isCollapsed(value) {
      if (value === false || value === 0) {
        this.scrollChatToBottom();
      }
    }
  },
  methods: {
    async getMessages(room_id) {
      try {
        this.isLoadingMessages = true;
        const response = await getChatMessages(room_id);

        if (response.status === 200) {
          this.messages = response.data.messages;
          this.totalMessages = response.data.messages_count;
          this.account_id = response.data.account_id;
          this.account_name = response.data.account_name;
          this.account_image = response.data.account_image;
          this.messageCounter = 15 // Initial number of messages
          this.isAllMessagesLoaded = false;

          this.scrollChatToBottom();
        }
      } catch (error) {
        console.error('Error loading messages:', error);
      } finally {
        this.isLoadingMessages = false;
      }
    },
    async getNewMessage(message) {
      const response = await getMessage(message);

      if (response.status === 200) {
        const newMessage = response.data.message;

        this.messages.push(newMessage);
        this.scrollChatToBottom();
      }
    },
    async loadMoreMessages() {
      try {
        this.isLoadingMoreMessages = true;
        const response = await getMoreChatMessages(this.room.id, this.messageCounter);

        if (response.status === 200) {
          this.messages = [...response.data.messages, ...this.messages];
          this.totalMessages = response.data.messages_count;
          this.messageCounter = this.messageCounter + response.data.retrieved_messages

          if (this.messages.length === this.totalMessages) {
            this.isAllMessagesLoaded = true;
          }
        }
      } catch (error) {
        console.error("Error loading more messages:", error);
      } finally {
        this.isLoadingMoreMessages = false;
      }
    },
    scrollMessages(event) {
      const chatMsg = event.target;
      const scrollHeight = chatMsg.scrollHeight;

      // Calculate the threshold for loading more messages
      const threshold = 100; // Adjust this value as needed

      if (
        !this.isAllMessagesLoaded &&
        chatMsg.scrollTop < threshold && // Check if scroll position is near the top
        chatMsg.scrollTop < this.prevScrollTop && // Check if scrolling up
        !this.isLoadingMoreMessages
      ) {
        // Set loading flag to prevent multiple loading at once
        this.isLoadingMoreMessages = true;

        this.loadMoreMessages()
          .then(() => {
            const newScrollHeight = chatMsg.scrollHeight;
            const newContentHeight = newScrollHeight - scrollHeight;
            // Adjust the scroll position to maintain the previous position
            chatMsg.scrollTop = this.prevScrollTop + newContentHeight;
          })
          .catch((error) => {
            console.error('Error loading more messages:', error);
          })
          .finally(() => {
            this.isLoadingMoreMessages = false;
          });
      }

      // Update the previous scroll position
      this.prevScrollTop = chatMsg.scrollTop;
    },
    scrollChatToBottom() {
      this.$nextTick(() => {
        const chatMsg = this.isFloating ? this.$refs.chatMessage : this.$parent.$refs.chatMessageBody;
        if (chatMsg) chatMsg.scrollTop = chatMsg.scrollHeight;
      });
    },
    sendingMessage(event) {
      const data = event.data;

      if (this.room.id === data.room_id && this.account_id === data.message.user_id) {
        let newMessage = {
          id: data.message.id,
          chat_room_id: data.message.chat_room_id,
          user_id: data.message.user_id,
          created_at: data.message.created_at,
          updated_at: data.message.updated_at,
          sender: {
            name: this.account_name,
          }
        };
        if (data.message.content) {
          newMessage.content = data.message.content;
        } else {
          newMessage.file = data.message.file;
          newMessage.file_name = data.message.file_name;
          newMessage.file_mime = data.message.file_mime;
        }
        this.messages.push(newMessage);
        this.scrollChatToBottom();
      }
    },
    messageSent(room_id, sender_id, message_id) {
      if (this.room.id === room_id && this.account_id !== sender_id) {
        this.getNewMessage(message_id);
      }
    }
  }
}
</script>
